<script lang="ts" setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  searchLocations,
  reverseGeocodeToAddressDto,
  type NominatimSearchResult
} from '../../composables/useNominatim'
import { regionConfig } from '@/config/region'
import type { BountyLocation, Address } from '@roadfootage/api-client'

const props = defineProps<{
  location?: BountyLocation | null
  address?: Address | null
}>()

const emit = defineEmits<{
  (e: 'update:location', val: BountyLocation): void
  (e: 'update:address', val: Address): void
}>()

const mapContainer = ref<HTMLElement | null>(null)
let mapInstance: L.Map | null = null
let radiusCircle: L.Circle | null = null
let geocodeTimer: ReturnType<typeof setTimeout> | null = null

const editable = ref(false)
const isGeocoding = ref(false)
const searchOptions = ref<NominatimSearchResult[]>([])
const selectedSearchResult = ref<NominatimSearchResult | null>(null)

function getInitialCenter(): [number, number] {
  const coords = props.location?.coordinates
  if (coords && coords.length >= 2) {
    // GeoJSON is [lng, lat]; Leaflet wants [lat, lng]
    return [coords[1], coords[0]]
  }
  const { sw, ne } = regionConfig.mapBounds
  return [(sw.lat + ne.lat) / 2, (sw.lng + ne.lng) / 2]
}

function setMapInteractive(interactive: boolean) {
  if (!mapInstance) return
  if (interactive) {
    mapInstance.dragging.enable()
    mapInstance.scrollWheelZoom.enable()
    mapInstance.doubleClickZoom.enable()
    mapInstance.touchZoom.enable()
    mapInstance.keyboard.enable()
    mapInstance.boxZoom.enable()
    mapInstance.on('moveend', onMoveEnd)
  } else {
    mapInstance.dragging.disable()
    mapInstance.scrollWheelZoom.disable()
    mapInstance.doubleClickZoom.disable()
    mapInstance.touchZoom.disable()
    mapInstance.keyboard.disable()
    mapInstance.boxZoom.disable()
    mapInstance.off('moveend', onMoveEnd)
    if (geocodeTimer) {
      clearTimeout(geocodeTimer)
      geocodeTimer = null
    }
    isGeocoding.value = false
  }
}

watch(editable, (val) => setMapInteractive(val))

function initMap() {
  if (!mapContainer.value) return

  const [startLat, startLng] = getInitialCenter()

  const streetLayer = new L.TileLayer(
    'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    { attribution: '&copy; OpenStreetMap contributors &copy; CARTO', maxZoom: 20 }
  )

  const satelliteLayer = new L.TileLayer(
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    {
      attribution:
        'Tiles &copy; Esri &mdash; Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP',
      maxZoom: 20
    }
  )

  const initialZoom = props.location?.coordinates ? 14 : 5

  mapInstance = new L.Map(mapContainer.value, {
    center: [startLat, startLng],
    zoom: initialZoom,
    zoomControl: false,
    layers: [satelliteLayer]
  })

  const regionBounds = new L.LatLngBounds(
    [regionConfig.mapBounds.sw.lat, regionConfig.mapBounds.sw.lng],
    [regionConfig.mapBounds.ne.lat, regionConfig.mapBounds.ne.lng]
  )
  mapInstance.setMaxBounds(regionBounds.pad(0.5))

  new L.Control.Layers({ 'Street Map': streetLayer, Satellite: satelliteLayer }, undefined, {
    position: 'topright'
  }).addTo(mapInstance)

  // Start locked — do not register moveend until editable is enabled
  setMapInteractive(false)

  if (props.location?.coordinates) {
    updateRadiusCircle(startLat, startLng)
  }
}

function updateRadiusCircle(lat: number, lng: number) {
  radiusCircle?.remove()
  if (!mapInstance) return
  radiusCircle = new L.Circle([lat, lng], {
    radius: 100,
    color: '#1565C0',
    weight: 1,
    fillColor: '#1565C0',
    fillOpacity: 0.12
  }).addTo(mapInstance)
}

function onMoveEnd() {
  const center = mapInstance?.getCenter()
  if (!center) return

  const lat = Number(center.lat.toFixed(6))
  const lng = Number(center.lng.toFixed(6))

  updateRadiusCircle(lat, lng)

  isGeocoding.value = true
  if (geocodeTimer) clearTimeout(geocodeTimer)
  geocodeTimer = setTimeout(async () => {
    try {
      const dto = await reverseGeocodeToAddressDto(lat, lng)
      emit('update:location', { type: 'Point', coordinates: [lng, lat] })
      emit('update:address', dto)
    } catch {
      // best-effort
    } finally {
      isGeocoding.value = false
    }
  }, 2000)
}

const onSearchFilter = async (val: string, doneFn: (cb: () => void) => void) => {
  if (!val || val.length < 2) {
    doneFn(() => {
      searchOptions.value = []
    })
    return
  }
  try {
    const results = await searchLocations(val)
    doneFn(() => {
      searchOptions.value = results
    })
  } catch {
    doneFn(() => {
      searchOptions.value = []
    })
  }
}

const onSearchSelect = (result: NominatimSearchResult | null) => {
  if (!result || !mapInstance) return
  const [south, north, west, east] = result.boundingbox.map(Number)
  mapInstance.fitBounds([
    [south, west],
    [north, east]
  ])
  nextTick(() => {
    selectedSearchResult.value = null
  })
}

onMounted(() => {
  initMap()
})

onBeforeUnmount(() => {
  if (geocodeTimer) clearTimeout(geocodeTimer)
  mapInstance?.remove()
  mapInstance = null
})
</script>

<template>
  <div>
    <div
      class="map-wrapper relative-position rounded-borders overflow-hidden bg-blue-grey-1 q-mb-md"
      style="height: 300px"
    >
      <!-- Search overlay — only shown when editable -->
      <div v-if="editable" class="map-search-overlay">
        <q-select
          v-model="selectedSearchResult"
          :options="searchOptions"
          option-label="display_name"
          use-input
          hide-selected
          fill-input
          input-debounce="500"
          placeholder="Search for a location..."
          dense
          outlined
          bg-color="white"
          class="search-select"
          @filter="onSearchFilter"
          @update:model-value="onSearchSelect"
        >
          <template #prepend>
            <q-icon name="mdi-magnify" size="xs" color="grey-6" />
          </template>
          <template #no-option>
            <q-item>
              <q-item-section class="text-grey">No results</q-item-section>
            </q-item>
          </template>
        </q-select>
      </div>

      <!-- Lock badge — only shown when read-only -->
      <div v-if="!editable" class="lock-badge">
        <q-icon name="mdi-lock" size="xs" color="grey-7" class="q-mr-xs" />
        <span class="text-caption text-grey-7">Read-only</span>
      </div>

      <div ref="mapContainer" class="full-height full-width" style="z-index: 10"></div>

      <!-- Center pin — only shown when editable -->
      <div
        v-if="editable"
        class="pointer-events-none center-pin-container flex flex-center"
        style="position: absolute; left: 50%; top: 50%; transform: translate(-50%, -100%)"
      >
        <q-icon name="mdi-map-marker" color="blue-8" size="50px" class="pin-drop-shadow" />
        <q-icon
          name="mdi-record-circle-outline"
          color="white"
          size="16px"
          class="absolute-center"
          style="margin-top: -6px"
        />
      </div>
    </div>

    <q-card flat bordered class="border-flat">
      <q-card-section class="q-pa-sm">
        <div class="row items-center justify-between q-mb-xs">
          <div class="col">
            <q-linear-progress v-if="isGeocoding" indeterminate color="blue-8" class="q-mb-xs" />
            <div v-if="address" class="text-body2 text-blue-grey-8">
              <q-icon name="mdi-map-marker-check" color="positive" class="q-mr-xs" />
              {{
                address.rawInput ||
                [address.streetName, address.suburb, address.state].filter(Boolean).join(', ')
              }}
            </div>
            <div v-else-if="!isGeocoding" class="text-body2 text-blue-grey-5">
              <q-icon name="mdi-map-marker-question" class="q-mr-xs" />
              {{ editable ? 'Pan the map to select a location' : 'No location set' }}
            </div>
          </div>
          <div class="col-auto">
            <q-toggle v-model="editable" label="Edit location" size="sm" />
          </div>
        </div>
      </q-card-section>
    </q-card>
  </div>
</template>

<style scoped>
.map-search-overlay {
  position: absolute;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1001;
  width: 300px;
}

.search-select {
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.lock-badge {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 1001;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 4px;
  padding: 2px 8px;
  display: flex;
  align-items: center;
}

.center-pin-container {
  z-index: 500;
}

.pin-drop-shadow {
  filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.4));
}

.border-flat {
  border: 1px solid var(--q-blue-grey-3);
}
</style>
